/*
 * Accessible newsletter popup — custom, WCAG-conscious replacement for the Klaviyo widget.
 * Submits to Klaviyo's client-side subscription API (public key only).
 *
 * Accessibility: role=dialog + aria-modal, focus moved in on open, focus trapped,
 * focus restored on close, Escape to close, `inert` when closed, polite live-region
 * status, labeled field with inline error announced via role="alert".
 */
(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var KLAVIYO_REVISION = '2024-10-15';

  var Popup = {
    root: null,
    card: null,
    previouslyFocused: null,
    submitting: false,

    init: function () {
      this.root = document.querySelector('[data-newsletter-popup]');
      if (!this.root) return;
      this.card = this.root.querySelector('[data-np-card]');

      this.publicKey = this.root.getAttribute('data-np-public-key');
      this.listId = this.root.getAttribute('data-np-list-id');
      this.delay = parseInt(this.root.getAttribute('data-np-delay'), 10) || 10000;
      this.snoozeDays = parseInt(this.root.getAttribute('data-np-snooze-days'), 10) || 7;

      this.bindEvents();
      this.scheduleOpen();
    },

    // ----- eligibility / scheduling -----
    storageGet: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    storageSet: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} },

    isEligible: function () {
      if (this.storageGet('np_subscribed') === '1') return false; // already signed up — never again
      var until = parseInt(this.storageGet('np_snooze_until') || '0', 10);
      if (until && Date.now() < until) return false; // dismissed recently
      return true;
    },

    // Auto-opening over another open dialog (cart drawer, lightbox, quickshop, mobile nav)
    // inerts the dialog the user is inside and tangles the Escape/focus handling of both.
    // Wait until no other modal is up before opening.
    anotherModalOpen: function () {
      return !!document.querySelector(
        '.pswp--open, ' +
        '[data-cart-drawer][aria-hidden="false"], ' +
        '#site-mobile-nav[data-open="true"], ' +
        '.modal-visible [data-modal-container], .modal-1-visible [data-modal-container-1]'
      );
    },

    scheduleOpen: function (delayOverride) {
      var self = this;
      if (!this.isEligible()) return;
      window.setTimeout(function () {
        if (!self.isEligible() || self.isOpen()) return;
        if (self.anotherModalOpen()) { self.scheduleOpen(15000); return; } // retry while a dialog is up
        self.open();
      }, typeof delayOverride === 'number' ? delayOverride : this.delay);
    },

    isOpen: function () { return this.root.getAttribute('aria-hidden') === 'false'; },

    // ----- open / close with focus management -----
    userIsTyping: function () {
      var ae = document.activeElement;
      return !!(ae && ae.matches && ae.matches('input:not([type=hidden]):not([type=checkbox]):not([type=radio]), textarea, select, [contenteditable="true"], [role="combobox"]'));
    },

    open: function () {
      if (this.isOpen()) return;
      if (this.anotherModalOpen() || this.userIsTyping()) { this.scheduleOpen(15000); return; }
      // Always open on a clean form state.
      this.showView('form');
      this.setError('');
      var resetBtn = this.root.querySelector('[data-np-submit]');
      if (resetBtn) { resetBtn.disabled = false; resetBtn.textContent = 'Get my $10 off'; }
      this.previouslyFocused = document.activeElement;
      this.root.removeAttribute('inert');
      this.root.setAttribute('aria-hidden', 'false');
      this.setBackgroundInert(true); // hide the rest of the page from AT while modal (WCAG 4.1.2/2.4.3)
      document.body.style.overflow = 'hidden'; // lock background scroll
      var card = this.card;
      requestAnimationFrame(function () { card.focus(); }); // announces the dialog
    },

    close: function (opts) {
      if (!this.isOpen()) return;
      this.root.setAttribute('aria-hidden', 'true');
      this.root.setAttribute('inert', '');
      this.setBackgroundInert(false);
      document.body.style.overflow = '';
      // Snooze unless this close is because they subscribed
      if (!(opts && opts.subscribed)) {
        this.storageSet('np_snooze_until', String(Date.now() + this.snoozeDays * 86400000));
      }
      // The popup auto-opens, so the remembered focus target is often <body> or a node
      // that no longer exists — fall back to a stable landmark instead of dropping focus.
      var prev = this.previouslyFocused;
      var prevUsable = prev && prev !== document.body && document.contains(prev) && typeof prev.focus === 'function';
      if (prevUsable) {
        prev.focus();
      } else {
        var fallback = document.querySelector('a.site-logo, header a[href], main h1, main');
        if (fallback) {
          if (fallback.tabIndex < 0 && !fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
          try { fallback.focus(); } catch (e) {}
        }
      }
      this.previouslyFocused = null;
    },

    // Make everything outside the popup inert/hidden to AT while the modal is open —
    // aria-modal alone is not honored by all AT, and a Tab trap doesn't stop the virtual cursor.
    setBackgroundInert: function (on) {
      var kids = document.body.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el === this.root || el.contains(this.root)) continue;
        if (/^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(el.tagName)) continue;
        // Never inert live regions — that silences announcements site-wide.
        if (el.hasAttribute('aria-live') || el.getAttribute('role') === 'status' || el.hasAttribute('data-cart-drawer-status')) continue;
        if (on) {
          if (!el.hasAttribute('inert')) {
            // Remember the EXACT prior aria-hidden value (true/false/absent) so restore
            // reinstates it — presence-only tracking turned aria-hidden="false" into "true".
            el.setAttribute('data-np-inerted', el.hasAttribute('aria-hidden') ? 'had:' + el.getAttribute('aria-hidden') : '1');
            el.setAttribute('inert', '');
            el.setAttribute('aria-hidden', 'true');
          }
        } else if (el.hasAttribute('data-np-inerted')) {
          var marker = el.getAttribute('data-np-inerted');
          el.removeAttribute('data-np-inerted');
          el.removeAttribute('inert');
          if (marker.indexOf('had:') === 0) el.setAttribute('aria-hidden', marker.slice(4));
          else el.removeAttribute('aria-hidden');
        }
      }
    },

    trapFocus: function (e) {
      var nodes = this.card.querySelectorAll(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      );
      var list = [];
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].offsetParent !== null || nodes[i].getClientRects().length) list.push(nodes[i]);
      }
      if (!list.length) { e.preventDefault(); this.card.focus(); return; }
      var first = list[0], last = list[list.length - 1], active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || active === this.card || !this.card.contains(active)) { e.preventDefault(); last.focus(); }
      } else if (active === last) { e.preventDefault(); first.focus(); }
    },

    announce: function (msg) {
      var region = this.root.querySelector('[data-np-status]');
      if (!region) return;
      region.textContent = '';
      window.setTimeout(function () { region.textContent = msg; }, 50);
    },

    showView: function (name) {
      var views = this.root.querySelectorAll('[data-np-view]');
      for (var i = 0; i < views.length; i++) {
        views[i].hidden = views[i].getAttribute('data-np-view') !== name;
      }
      // Re-point the dialog's accessible name/description at the VISIBLE view's heading/text,
      // otherwise aria-labelledby="np-title" references a now-hidden element (deep audit #14).
      var card = this.root.querySelector('[role="dialog"]') || this.card || this.root;
      if (card) {
        if (name === 'success') {
          card.setAttribute('aria-labelledby', 'np-title-success');
          card.setAttribute('aria-describedby', 'np-desc-success');
        } else {
          card.setAttribute('aria-labelledby', 'np-title');
          card.setAttribute('aria-describedby', 'np-desc');
        }
      }
    },

    // ----- submit -----
    validEmail: function (v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); },

    setError: function (msg) {
      var input = this.root.querySelector('#np-email');
      var err = this.root.querySelector('#np-error');
      if (msg) {
        if (err) { err.textContent = msg; err.hidden = false; }
        if (input) input.setAttribute('aria-invalid', 'true');
      } else {
        if (err) { err.textContent = ''; err.hidden = true; }
        if (input) input.removeAttribute('aria-invalid');
      }
    },

    submit: function (form) {
      var self = this;
      if (this.submitting) return;
      var input = form.querySelector('#np-email');
      var btn = form.querySelector('[data-np-submit]');
      var email = (input.value || '').trim();

      if (!this.validEmail(email)) {
        this.setError('Please enter a valid email address.');
        input.focus();
        return;
      }
      this.setError('');
      this.submitting = true;
      if (btn) { btn.disabled = true; btn.textContent = 'Signing you up…'; }

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Website popup ($10 off)',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } }
        }
      };

      fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(this.publicKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          self.submitting = false;
          if (res.status === 202 || res.ok) {
            self.storageSet('np_subscribed', '1');
            self.showView('success');
            self.announce("You're subscribed. Check your email for your $10-off code.");
            var ok = self.root.querySelector('[data-np-view="success"] [data-np-close]');
            if (ok) requestAnimationFrame(function () { ok.focus(); });
          } else {
            return res.text().then(function (t) { throw new Error(t || ('HTTP ' + res.status)); });
          }
        })
        .catch(function (err) {
          self.submitting = false;
          if (btn) { btn.disabled = false; btn.textContent = 'Get my $10 off'; }
          self.setError('Something went wrong — please try again.');
          self.announce('Sign-up failed. Please try again.');
          // Disabling the focused submit dropped focus to <body> inside the open modal —
          // put the keyboard user back on the email field (WCAG 2.4.3).
          var inp = self.root.querySelector('#np-email');
          if (inp) { try { inp.focus(); } catch (e) {} }
          if (window.console) console.error('Newsletter popup submit error:', err);
        });
    },

    // ----- events -----
    bindEvents: function () {
      var self = this;

      this.root.addEventListener('click', function (e) {
        if (e.target.closest('[data-np-close]')) { e.preventDefault(); self.close(); }
      });

      this.root.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-np-form]');
        if (form) { e.preventDefault(); self.submit(form); }
      });

      // clear the error as the user types
      this.root.addEventListener('input', function (e) {
        if (e.target.id === 'np-email') self.setError('');
      });

      // Capture phase + stopPropagation: while the popup is the top dialog, its Escape/Tab
      // must NOT also reach other dialogs' handlers (one Escape was closing two dialogs).
      document.addEventListener('keydown', function (e) {
        if (!self.isOpen()) return;
        if (e.key === 'Escape') { e.stopPropagation(); self.close(); }
        else if (e.key === 'Tab') self.trapFocus(e);
      }, true);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Popup.init(); });
  } else {
    Popup.init();
  }

  // Expose a manual opener for testing / custom triggers
  window.NewsletterPopup = Popup;
})();
